import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import DateRangeFilter from "@/components/DateRangeFilter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, TrendingUp, LogOut, Star, MessageSquare, QrCode, Download, Banknote, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import StarRating from "@/components/StarRating";
import { QRCodeCanvas } from "qrcode.react";

const CUISINES = ["pizza", "burgers", "fish-chips", "indian", "steak", "mexican", "healthy", "desserts", "drinks"];
const STATUSES = ["received", "confirmed", "preparing", "collecting", "on-the-way", "delivered"];

const emptyRestaurant = {
  name: "", slug: "", cuisine: "pizza", cover_image: "", logo: "", description: "",
  rating: 4.5, delivery_time: "30-45 min", delivery_fee: 2.5, min_order: 10,
  opening_hours: { mon: { open: "12:00", close: "23:00" }, tue: { open: "12:00", close: "23:00" }, wed: { open: "12:00", close: "23:00" }, thu: { open: "12:00", close: "23:00" }, fri: { open: "12:00", close: "23:00" }, sat: { open: "12:00", close: "23:00" }, sun: { open: "12:00", close: "23:00" } },
  featured: false, is_active: true, address: "Caleta de Fuste", phone: "",
};

const emptyItem = {
  restaurant_id: "", category: "Pizza", name: "", description: "", price: 0, image: "",
  is_popular: false, is_family_deal: false, is_active: true,
  addons: [], removable_ingredients: [],
};

const CATEGORY_SUGGESTIONS = ["Pizza", "Starters", "Drinks", "Desserts", "Popular", "Family Meals", "Main Courses"];

const Admin = () => {
  const { t } = useLang();
  const { admin, logout } = useAuth();
  const nav = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ count: 0, revenue: 0, by_status: {}, orders: [] });
  const [promos, setPromos] = useState([]);
  const [restaurantUsers, setRestaurantUsers] = useState([]);
  const [newRUser, setNewRUser] = useState({ email: "", password: "", restaurant_id: "", name: "" });
  const [reviewData, setReviewData] = useState({ count: 0, avg_rider: null, avg_app: null, reviews: [] });
  const [riderUsers, setRiderUsers] = useState([]);
  const [newRider, setNewRider] = useState({ email: "", password: "", name: "", phone: "" });
  const [riderEarnings, setRiderEarnings] = useState([]);
  const [platformSettings, setPlatformSettings] = useState({});
  const [settingsDraft, setSettingsDraft] = useState({
    whatsapp_business_number: "", twilio_account_sid: "", twilio_auth_token: "",
    twilio_whatsapp_from: "", public_app_url: "",
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [whatsappLog, setWhatsappLog] = useState([]);

  const [editRest, setEditRest] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [newPromo, setNewPromo] = useState({ code: "", discount_percent: 10, description: "", active: true });
  const [selectedRest, setSelectedRest] = useState("");
  const [qrHotel, setQrHotel] = useState("");
  const [commissionSummary, setCommissionSummary] = useState([]);
  const [commissionFilter, setCommissionFilter] = useState("pending"); // pending | paid | all
  const [settleTarget, setSettleTarget] = useState(null); // {restaurant_id, restaurant_name, pending}
  const [transferDate, setTransferDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [settlements, setSettlements] = useState([]);
  const [settling, setSettling] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [riderDetail, setRiderDetail] = useState(null); // {rider, orders, totals}
  const [riderDetailLoading, setRiderDetailLoading] = useState(false);
  const [selectedRiderOrders, setSelectedRiderOrders] = useState([]);
  const [riderPaidAt, setRiderPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [settlingRider, setSettlingRider] = useState(false);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [detailSettlement, setDetailSettlement] = useState(null);
  const [detailReview, setDetailReview] = useState(null);

  const loadCommissions = async () => {
    try {
      const [sumRes, settRes] = await Promise.all([
        api.get("/admin/commission-summary"),
        api.get("/admin/settlements"),
      ]);
      setCommissionSummary(sumRes.data);
      setSettlements(settRes.data);
    } catch { /* silent */ }
  };

  const confirmSettle = async () => {
    if (!settleTarget || !transferDate) return;
    setSettling(true);
    try {
      await api.post("/admin/settle-restaurant", {
        restaurant_id: settleTarget.restaurant_id,
        transfer_date: transferDate,
      });
      toast.success(`Saldato ${settleTarget.restaurant_name} — email inviata`);
      setSettleTarget(null);
      await loadCommissions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Settlement failed");
    } finally {
      setSettling(false);
    }
  };

  const load = async () => {
    const params = { params: dateRange };
    const [r, o, s, p, ru, rv, rd] = await Promise.all([
      api.get("/restaurants"),
      api.get("/orders", params),
      api.get("/orders-summary/today", params),
      api.get("/promotions"),
      api.get("/admin/restaurant-users"),
      api.get("/admin/reviews"),
      api.get("/admin/rider-users"),
    ]);
    setRestaurants(r.data); setOrders(o.data); setSummary(s.data); setPromos(p.data); setRestaurantUsers(ru.data); setReviewData(rv.data); setRiderUsers(rd.data);
    if (r.data[0] && !selectedRest) setSelectedRest(r.data[0].id);
  };

  const addRider = async () => {
    if (!newRider.email || !newRider.password) { toast.error("Email and password required"); return; }
    try {
      await api.post("/admin/rider-users", newRider);
      toast.success("Rider account created");
      setNewRider({ email: "", password: "", name: "", phone: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delRider = async (id) => { if (!confirm("Delete this rider login?")) return; await api.delete(`/admin/rider-users/${id}`); toast.success("Deleted"); load(); };
  const resetRiderPassword = async (id) => {
    const pw = prompt("New password:"); if (!pw) return;
    try { await api.put(`/admin/rider-users/${id}/password`, { password: pw }); toast.success("Password updated"); } catch { toast.error("Failed"); }
  };
  const updateRiderPhone = async (id, phone) => {
    try {
      await api.patch(`/admin/rider-users/${id}`, { phone });
      toast.success("Telefono aggiornato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const assignRider = async (num, riderId) => {
    await api.patch(`/orders/${num}/assign-rider`, { rider_id: riderId || null });
    toast.success(riderId ? "Rider assigned" : "Rider cleared");
    load();
  };

  const loadWhatsappSettings = async () => {
    try {
      const [s, e, l] = await Promise.all([
        api.get("/admin/platform-settings"),
        api.get("/admin/rider-earnings", { params: dateRange }),
        api.get("/admin/whatsapp-log?limit=30"),
      ]);
      setPlatformSettings(s.data);
      setSettingsDraft({
        whatsapp_business_number: s.data.whatsapp_business_number || "",
        twilio_account_sid: s.data.twilio_account_sid || "",
        twilio_auth_token: "",
        twilio_whatsapp_from: s.data.twilio_whatsapp_from || "",
        public_app_url: s.data.public_app_url || "",
      });
      setRiderEarnings(e.data);
      setWhatsappLog(l.data);
    } catch { /* silent */ }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const body = { ...settingsDraft };
      if (!body.twilio_auth_token) delete body.twilio_auth_token;  // leave unchanged if blank
      const r = await api.put("/admin/platform-settings", body);
      setPlatformSettings(r.data);
      setSettingsDraft(prev => ({ ...prev, twilio_auth_token: "" }));
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSavingSettings(false); }
  };

  const openRiderDetail = async (riderId) => {
    setRiderDetailLoading(true);
    setRiderDetail({ rider: { id: riderId, name: "…" }, orders: [], totals: {} });
    setSelectedRiderOrders([]);
    setRiderPaidAt(new Date().toISOString().slice(0, 10));
    try {
      const r = await api.get(`/admin/rider-detail/${riderId}`, { params: dateRange });
      setRiderDetail(r.data);
      // Preselect all unpaid orders
      setSelectedRiderOrders(r.data.orders.filter(o => !o.rider_paid_at).map(o => o.order_number));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento rider"); setRiderDetail(null); }
    finally { setRiderDetailLoading(false); }
  };

  const toggleRiderOrderSelection = (num, paid) => {
    if (paid) return;
    setSelectedRiderOrders(prev => prev.includes(num) ? prev.filter(x => x !== num) : [...prev, num]);
  };

  const confirmRiderSettlement = async () => {
    if (!riderDetail || selectedRiderOrders.length === 0) { toast.error("Seleziona almeno un ordine"); return; }
    if (!riderPaidAt) { toast.error("Seleziona la data del pagamento"); return; }
    if (!window.confirm(`Confermi il pagamento di ${selectedRiderOrders.length} consegne a ${riderDetail.rider.name}?`)) return;
    setSettlingRider(true);
    try {
      const res = await api.post("/admin/rider-settlements", {
        rider_id: riderDetail.rider.id,
        order_numbers: selectedRiderOrders,
        paid_at: riderPaidAt,
      });
      toast.success(`Saldato €${res.data.total_amount.toFixed(2)} — WhatsApp inviato al rider`);
      await openRiderDetail(riderDetail.rider.id);
      loadWhatsappSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSettlingRider(false); }
  };

  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);
  useEffect(() => { loadCommissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);
  useEffect(() => { loadWhatsappSettings(); const iv = setInterval(loadWhatsappSettings, 20000); return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);
  // If the rider detail dialog is open, refresh its content when the date range changes.
  useEffect(() => {
    if (riderDetail?.rider?.id) {
      openRiderDetail(riderDetail.rider.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);
  useEffect(() => { if (selectedRest) api.get(`/restaurants/${selectedRest}/menu`).then(r => setItems(r.data)); }, [selectedRest]);

  const saveRest = async () => {
    const data = { ...editRest };
    if (!data.slug) data.slug = data.name.toLowerCase().replace(/\s+/g, "-");
    try {
      if (data.id) await api.put(`/restaurants/${data.id}`, data);
      else await api.post("/restaurants", data);
      toast.success("Saved"); setEditRest(null); load();
    } catch { toast.error("Failed"); }
  };
  const delRest = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/restaurants/${id}`); toast.success("Deleted"); load(); };

  const saveItem = async () => {
    const data = { ...editItem, restaurant_id: editItem.restaurant_id || selectedRest };
    try {
      if (data.id) await api.put(`/menu-items/${data.id}`, data);
      else await api.post("/menu-items", data);
      toast.success("Saved"); setEditItem(null);
      const r = await api.get(`/restaurants/${selectedRest}/menu`); setItems(r.data);
    } catch { toast.error("Failed"); }
  };
  const delItem = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/menu-items/${id}`); toast.success("Deleted"); const r = await api.get(`/restaurants/${selectedRest}/menu`); setItems(r.data); };

  const updateOrderStatus = async (num, status) => { await api.patch(`/orders/${num}/status`, { status }); toast.success("Updated"); load(); };

  const addPromo = async () => {
    if (!newPromo.code) return;
    await api.post("/promotions", { ...newPromo, code: newPromo.code.toUpperCase() });
    setNewPromo({ code: "", discount_percent: 10, description: "", active: true });
    load();
  };
  const delPromo = async (id) => { await api.delete(`/promotions/${id}`); load(); };

  const addRestaurantUser = async () => {
    if (!newRUser.email || !newRUser.password || !newRUser.restaurant_id) { toast.error("Email, password and restaurant are required"); return; }
    try {
      await api.post("/admin/restaurant-users", newRUser);
      toast.success("Restaurant account created");
      setNewRUser({ email: "", password: "", restaurant_id: "", name: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delRestaurantUser = async (id) => { if (!confirm("Delete this restaurant login?")) return; await api.delete(`/admin/restaurant-users/${id}`); toast.success("Deleted"); load(); };
  const resetRestaurantUserPassword = async (id) => {
    const pw = prompt("New password:");
    if (!pw) return;
    try { await api.put(`/admin/restaurant-users/${id}/password`, { password: pw }); toast.success("Password updated"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-navy tracking-tight">Admin Dashboard</h1>
          <p className="text-navy/60 text-sm mt-1">Signed in as <span className="font-semibold text-navy">{admin?.email}</span></p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangeFilter
            start={dateRange.start}
            end={dateRange.end}
            onChange={setDateRange}
            testIdPrefix="admin-range"
          />
          <Button
            variant="outline"
            onClick={() => { logout(); nav("/admin/login"); }}
            className="rounded-full border-2 border-navy text-navy hover:bg-navy hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-1" /> Log out
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="mt-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="restaurants" data-testid="tab-restaurants">Restaurants</TabsTrigger>
          <TabsTrigger value="menu" data-testid="tab-menu">Menu</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="promotions" data-testid="tab-promos">Promotions</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts">Restaurant Accounts</TabsTrigger>
          <TabsTrigger value="riders" data-testid="tab-riders">Rider Accounts</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="tab-commissions">Commissions</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="qr" data-testid="tab-qr">QR Codes</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-navy/10">
              <div className="text-xs uppercase text-navy/50">{t("orderCount")} {(dateRange.start || dateRange.end) ? "(nel periodo)" : "(today)"}</div>
              <div className="font-heading text-3xl font-extrabold text-navy mt-1" data-testid="summary-count">{summary.count}</div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-navy/10">
              <div className="text-xs uppercase text-navy/50">{t("revenue")} {(dateRange.start || dateRange.end) ? "(nel periodo)" : "(today)"}</div>
              <div className="font-heading text-3xl font-extrabold text-coral mt-1" data-testid="summary-revenue">€{summary.revenue.toFixed(2)}</div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-navy/10">
              <div className="text-xs uppercase text-navy/50">Restaurants</div>
              <div className="font-heading text-3xl font-extrabold text-navy mt-1">{restaurants.length}</div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-navy/10">
            <h3 className="font-heading font-bold text-navy mb-3">{(dateRange.start || dateRange.end) ? `Ordini dal ${dateRange.start || "…"} al ${dateRange.end || "…"}` : "Today's orders"}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-navy/50">
                  <tr><th className="py-2">#</th><th>Data/ora</th><th>Restaurant</th><th>Customer</th><th>Total</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {summary.orders.map(o => (
                    <tr
                      key={o.order_number}
                      className="border-t border-navy/5 cursor-pointer hover:bg-sand-50 transition"
                      onClick={() => setDetailOrder(o)}
                      data-testid={`overview-order-row-${o.order_number}`}
                    >
                      <td className="py-2 font-mono">{o.order_number}</td>
                      <td className="text-xs text-navy/70 whitespace-nowrap" data-testid={`overview-order-date-${o.order_number}`}>{new Date(o.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                      <td>{o.restaurant_name}</td>
                      <td>{o.customer.first_name} {o.customer.last_name}</td>
                      <td>€{o.total.toFixed(2)}</td>
                      <td><span className="text-xs bg-sand-100 px-2 py-0.5 rounded-full">{o.status}</span></td>
                    </tr>
                  ))}
                  {summary.orders.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-navy/40">No orders yet today</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="restaurants">
          <Button onClick={() => setEditRest({ ...emptyRestaurant })} className="btn-coral mb-4" data-testid="add-restaurant-btn"><Plus className="w-4 h-4 mr-1" /> Add restaurant</Button>
          <div className="grid gap-3">
            {restaurants.map(r => (
              <div key={r.id} className="bg-white p-4 rounded-2xl border border-navy/10 flex gap-4 items-center">
                <img src={r.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-navy">{r.name}</div>
                  <div className="text-xs text-navy/60 capitalize">{r.cuisine} • €{r.delivery_fee.toFixed(2)} delivery • Min €{r.min_order.toFixed(2)}</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditRest(r)} data-testid={`edit-restaurant-${r.slug}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => delRest(r.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="menu" className="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Restaurant</Label>
              <Select value={selectedRest} onValueChange={setSelectedRest}>
                <SelectTrigger data-testid="menu-restaurant-select"><SelectValue /></SelectTrigger>
                <SelectContent>{restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={() => setEditItem({ ...emptyItem, restaurant_id: selectedRest })} className="btn-coral"><Plus className="w-4 h-4 mr-1" /> Add item</Button>
          </div>
          <div className="grid gap-2">
            {items.map(it => (
              <div key={it.id} className="bg-white p-3 rounded-2xl border border-navy/10 flex gap-3 items-center">
                {it.image && <img src={it.image} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-navy">{it.name} <span className="text-xs font-normal text-navy/50">— {it.category}</span></div>
                  <div className="text-sm text-navy/70">€{it.price.toFixed(2)}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setEditItem(it)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => delItem(it.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="bg-white rounded-2xl border border-navy/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-navy/50 bg-sand-50">
                <tr><th className="p-3">#</th><th>Data/ora</th><th>Restaurant</th><th>Customer</th><th>Delivery</th><th>Rider</th><th>Total</th><th>Status</th></tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr
                    key={o.order_number}
                    className="border-t border-navy/5 cursor-pointer hover:bg-sand-50 transition"
                    onClick={() => setDetailOrder(o)}
                    data-testid={`orders-row-${o.order_number}`}
                  >
                    <td className="p-3 font-mono">{o.order_number}</td>
                    <td className="text-xs text-navy/70 whitespace-nowrap" data-testid={`orders-date-${o.order_number}`}>{new Date(o.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                    <td>{o.restaurant_name}</td>
                    <td>{o.customer.first_name} {o.customer.last_name}</td>
                    <td className="text-xs text-navy/60">{o.delivery.accommodation_type} {o.delivery.room}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <Select value={o.assigned_rider_id || "none"} onValueChange={v => assignRider(o.order_number, v === "none" ? null : v)}>
                        <SelectTrigger className="h-8 w-36" data-testid={`admin-assign-rider-${o.order_number}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Unassigned —</SelectItem>
                          {riderUsers.map(r => <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td>€{o.total.toFixed(2)}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <Select value={o.status} onValueChange={v => updateOrderStatus(o.order_number, v)}>
                        <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-navy/40">No orders yet</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="promotions" className="space-y-4">
          <div className="bg-white p-4 rounded-2xl border border-navy/10 grid sm:grid-cols-4 gap-2 items-end">
            <div><Label>Code</Label><Input value={newPromo.code} onChange={e => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })} /></div>
            <div><Label>% off</Label><Input type="number" value={newPromo.discount_percent} onChange={e => setNewPromo({ ...newPromo, discount_percent: Number(e.target.value) })} /></div>
            <div><Label>Description</Label><Input value={newPromo.description} onChange={e => setNewPromo({ ...newPromo, description: e.target.value })} /></div>
            <Button onClick={addPromo} className="btn-coral">Add promo</Button>
          </div>
          <div className="grid gap-2">
            {promos.map(p => (
              <div key={p.id} className="bg-white p-3 rounded-2xl border border-navy/10 flex justify-between items-center">
                <div><span className="font-heading font-bold text-navy">{p.code}</span> <span className="text-navy/60 text-sm">— {p.discount_percent}% off — {p.description}</span></div>
                <Button variant="outline" size="sm" onClick={() => delPromo(p.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="accounts" className="space-y-4">
          <p className="text-sm text-navy/60">Create login credentials for each restaurant so their team can manage their own menu, hours and orders.</p>
          <div className="bg-white p-4 rounded-2xl border border-navy/10 grid sm:grid-cols-5 gap-2 items-end">
            <div><Label>Restaurant</Label>
              <Select value={newRUser.restaurant_id} onValueChange={v => setNewRUser({ ...newRUser, restaurant_id: v })}>
                <SelectTrigger data-testid="new-ruser-restaurant"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Email</Label><Input type="email" value={newRUser.email} onChange={e => setNewRUser({ ...newRUser, email: e.target.value })} data-testid="new-ruser-email" /></div>
            <div><Label>Password</Label><Input type="text" value={newRUser.password} onChange={e => setNewRUser({ ...newRUser, password: e.target.value })} data-testid="new-ruser-password" /></div>
            <div><Label>Name (optional)</Label><Input value={newRUser.name} onChange={e => setNewRUser({ ...newRUser, name: e.target.value })} /></div>
            <Button onClick={addRestaurantUser} className="btn-coral" data-testid="add-ruser-btn">Create account</Button>
          </div>
          <div className="grid gap-2">
            {restaurantUsers.map(u => {
              const r = restaurants.find(x => x.id === u.restaurant_id);
              return (
                <div key={u.id} className="bg-white p-3 rounded-2xl border border-navy/10 flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <div className="font-heading font-bold text-navy">{u.email}</div>
                    <div className="text-xs text-navy/60">Restaurant: <span className="font-semibold">{r?.name || "—"}</span></div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => resetRestaurantUserPassword(u.id)}>Reset password</Button>
                    <Button variant="outline" size="sm" onClick={() => delRestaurantUser(u.id)} data-testid={`del-ruser-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            })}
            {restaurantUsers.length === 0 && <p className="text-center text-navy/40 py-6 text-sm">No restaurant logins yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="riders" className="space-y-4">
          <p className="text-sm text-navy/60">Create login credentials for each rider. Riders sign in at <span className="font-mono">/rider/login</span> and only see the deliveries assigned to them. Il numero di telefono con prefisso internazionale (+34…, +44…) è usato per inviare i nuovi ordini via WhatsApp.</p>
          <div className="bg-white p-4 rounded-2xl border border-navy/10 grid sm:grid-cols-5 gap-2 items-end">
            <div><Label>Email</Label><Input type="email" value={newRider.email} onChange={e => setNewRider({ ...newRider, email: e.target.value })} data-testid="new-rider-email" /></div>
            <div><Label>Password</Label><Input type="text" value={newRider.password} onChange={e => setNewRider({ ...newRider, password: e.target.value })} data-testid="new-rider-password" /></div>
            <div><Label>Name</Label><Input value={newRider.name} onChange={e => setNewRider({ ...newRider, name: e.target.value })} data-testid="new-rider-name" /></div>
            <div><Label>WhatsApp (+34…)</Label><Input value={newRider.phone} onChange={e => setNewRider({ ...newRider, phone: e.target.value })} placeholder="+34612345678" data-testid="new-rider-phone" /></div>
            <Button onClick={addRider} className="btn-coral" data-testid="add-rider-btn">Create rider</Button>
          </div>
          <div className="grid gap-2">
            {riderUsers.map(u => {
              const earn = riderEarnings.find(e => e.rider_id === u.id);
              return (
                <div
                  key={u.id}
                  className="bg-white p-3 rounded-2xl border border-navy/10 flex justify-between items-center flex-wrap gap-3 cursor-pointer hover:border-coral hover:shadow-md transition"
                  onClick={() => openRiderDetail(u.id)}
                  data-testid={`rider-row-${u.id}`}
                >
                  <div className="min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <div className="font-heading font-bold text-navy">{u.name || u.email}</div>
                      {u.is_online ? <span className="text-[10px] uppercase font-bold bg-teal/15 text-teal px-1.5 py-0.5 rounded" data-testid={`rider-online-badge-${u.id}`}>● Online</span> : <span className="text-[10px] uppercase font-bold bg-navy/10 text-navy/50 px-1.5 py-0.5 rounded">○ Offline</span>}
                    </div>
                    <div className="text-xs text-navy/60">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-[220px]" onClick={e => e.stopPropagation()}>
                    <Label className="text-xs whitespace-nowrap">Phone:</Label>
                    <Input
                      defaultValue={u.phone || ""}
                      placeholder="+34..."
                      className="h-8 font-mono text-sm"
                      onBlur={(e) => { if (e.target.value !== (u.phone || "")) updateRiderPhone(u.id, e.target.value); }}
                      data-testid={`rider-phone-${u.id}`}
                    />
                  </div>
                  {earn && (
                    <div className="text-right pr-2">
                      <div className="text-[10px] uppercase text-navy/40">Da saldare / Totale</div>
                      <div className="font-heading font-bold text-navy text-sm">
                        <span className="text-coral" data-testid={`rider-unpaid-${u.id}`}>€{(earn.unpaid_total ?? 0).toFixed(2)}</span>
                        <span className="text-navy/40 text-xs mx-1">/</span>
                        <span className="text-teal">€{earn.total.toFixed(2)}</span>
                        <span className="text-[10px] text-navy/50 font-normal ml-1">({earn.orders_count})</span>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                    <Button variant="outline" size="sm" onClick={() => resetRiderPassword(u.id)}>Reset password</Button>
                    <Button variant="outline" size="sm" onClick={() => delRider(u.id)} data-testid={`del-rider-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            })}
            {riderUsers.length === 0 && <p className="text-center text-navy/40 py-6 text-sm">No rider logins yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="reviews" className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-navy/10">
              <div className="text-xs uppercase text-navy/50">Total reviews</div>
              <div className="font-heading text-3xl font-extrabold text-navy mt-1" data-testid="review-count">{reviewData.count}</div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-navy/10">
              <div className="text-xs uppercase text-navy/50">Avg rider rating</div>
              <div className="font-heading text-3xl font-extrabold text-navy mt-1 flex items-center gap-2">
                {reviewData.avg_rider ?? "—"} {reviewData.avg_rider && <Star className="w-5 h-5 fill-amber-400 text-amber-400" />}
              </div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-navy/10">
              <div className="text-xs uppercase text-navy/50">Avg app rating</div>
              <div className="font-heading text-3xl font-extrabold text-navy mt-1 flex items-center gap-2">
                {reviewData.avg_app ?? "—"} {reviewData.avg_app && <Star className="w-5 h-5 fill-amber-400 text-amber-400" />}
              </div>
            </div>
          </div>
          <p className="text-xs text-navy/50">Rider and app ratings/comments are visible only to admins. Restaurant reviews are public on the restaurant page.</p>
          <div className="grid gap-2">
            {reviewData.reviews.map(rv => (
              <div
                key={rv.id}
                className="bg-white p-4 rounded-2xl border border-navy/10 cursor-pointer hover:border-coral hover:shadow-md transition"
                onClick={() => setDetailReview(rv)}
                data-testid={`admin-review-${rv.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-heading font-bold text-navy">{rv.customer_first_name || "Guest"} — <span className="text-navy/60 text-sm">#{rv.order_number}</span></div>
                    <div className="text-[11px] text-navy/50">{new Date(rv.created_at).toLocaleString()} — {rv.restaurant_name}</div>
                  </div>
                </div>
                <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
                  <div className="p-3 bg-sand-50 rounded-lg">
                    <div className="text-[11px] uppercase text-navy/50 font-bold">Restaurant <span className="text-teal">(public)</span></div>
                    <StarRating value={rv.restaurant_rating} readOnly size="sm" />
                    {rv.restaurant_comment && <p className="text-xs text-navy/70 mt-1 italic">"{rv.restaurant_comment}"</p>}
                  </div>
                  <div className="p-3 bg-sand-50 rounded-lg">
                    <div className="text-[11px] uppercase text-navy/50 font-bold">Rider <span className="text-coral">(admin only)</span></div>
                    {rv.rider_rating ? <StarRating value={rv.rider_rating} readOnly size="sm" /> : <span className="text-xs text-navy/40">Not rated</span>}
                    {rv.rider_comment && <p className="text-xs text-navy/70 mt-1 italic">"{rv.rider_comment}"</p>}
                  </div>
                  <div className="p-3 bg-sand-50 rounded-lg">
                    <div className="text-[11px] uppercase text-navy/50 font-bold">App <span className="text-coral">(admin only)</span></div>
                    {rv.app_rating ? <StarRating value={rv.app_rating} readOnly size="sm" /> : <span className="text-xs text-navy/40">Not rated</span>}
                    {rv.app_comment && <p className="text-xs text-navy/70 mt-1 italic">"{rv.app_comment}"</p>}
                  </div>
                </div>
              </div>
            ))}
            {reviewData.reviews.length === 0 && (
              <div className="bg-white p-8 rounded-2xl border border-navy/10 text-center">
                <MessageSquare className="w-8 h-8 text-navy/20 mx-auto mb-2" />
                <p className="text-navy/50 text-sm">No reviews yet. They will appear here after customers rate a delivered order.</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="commissions" className="space-y-4">
          <div className="bg-white p-5 rounded-2xl border border-navy/10">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center shrink-0">
                <Banknote className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-navy text-lg">Riepilogo commissioni per ristorante</h3>
                <p className="text-sm text-navy/60 mt-0.5">Commissione piattaforma: <b>20%</b> sui prodotti ordinati (delivery escluso). Marca un ristorante come <b>Saldato</b> per registrare il bonifico e inviare la conferma via email.</p>
              </div>
            </div>

            {/* Filter */}
            <div className="inline-flex bg-sand-100 p-1 rounded-full text-xs font-semibold mb-4">
              <button
                onClick={() => setCommissionFilter("pending")}
                className={`px-4 py-1.5 rounded-full transition ${commissionFilter === "pending" ? "bg-coral text-white" : "text-navy/60 hover:text-navy"}`}
                data-testid="filter-pending"
              >Da saldare</button>
              <button
                onClick={() => setCommissionFilter("paid")}
                className={`px-4 py-1.5 rounded-full transition ${commissionFilter === "paid" ? "bg-teal text-white" : "text-navy/60 hover:text-navy"}`}
                data-testid="filter-paid"
              >Saldato</button>
              <button
                onClick={() => setCommissionFilter("all")}
                className={`px-4 py-1.5 rounded-full transition ${commissionFilter === "all" ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`}
                data-testid="filter-all"
              >Tutti</button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-navy/50 bg-sand-50">
                  <tr>
                    <th className="p-3">Ristorante</th>
                    <th className="text-right">Ordini</th>
                    <th className="text-right">Prodotti €</th>
                    <th className="text-right">Commissione (20%)</th>
                    <th className="text-right">Netto</th>
                    <th>Stato</th>
                    <th>Azione</th>
                  </tr>
                </thead>
                <tbody>
                  {commissionSummary
                    .filter(r => {
                      if (commissionFilter === "pending") return r.pending.orders_count > 0;
                      if (commissionFilter === "paid") return r.settled.orders_count > 0;
                      return true;
                    })
                    .map(r => {
                      const block = commissionFilter === "paid" ? r.settled : r.pending;
                      const isPending = commissionFilter !== "paid" && r.pending.orders_count > 0;
                      return (
                        <tr
                          key={r.restaurant_id}
                          className="border-t border-navy/5 cursor-pointer hover:bg-sand-50 transition"
                          onClick={() => { setSettleTarget(r); setTransferDate(new Date().toISOString().slice(0, 10)); }}
                          data-testid={`commission-row-${r.restaurant_id}`}
                        >
                          <td className="p-3 font-semibold text-navy">{r.restaurant_name}
                            <div className="text-[11px] text-navy/40 font-normal">{r.restaurant_email || "no email set"}</div>
                          </td>
                          <td className="text-right">{block.orders_count}</td>
                          <td className="text-right font-mono">€{block.products_total.toFixed(2)}</td>
                          <td className="text-right font-mono text-coral">-€{block.commission_total.toFixed(2)}</td>
                          <td className="text-right font-mono text-teal font-bold">€{block.net_total.toFixed(2)}</td>
                          <td>
                            {isPending ? (
                              <span className="inline-flex items-center gap-1 bg-coral/10 text-coral text-[11px] font-bold px-2 py-1 rounded-full">
                                <Clock className="w-3 h-3" /> Da saldare
                              </span>
                            ) : r.settled.orders_count > 0 ? (
                              <span className="inline-flex items-center gap-1 bg-teal/10 text-teal text-[11px] font-bold px-2 py-1 rounded-full">
                                <CheckCircle2 className="w-3 h-3" /> Saldato
                              </span>
                            ) : (
                              <span className="text-[11px] text-navy/40">—</span>
                            )}
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            {isPending && (
                              <Button
                                size="sm"
                                className="btn-coral h-8 text-xs"
                                onClick={() => { setSettleTarget(r); setTransferDate(new Date().toISOString().slice(0, 10)); }}
                                data-testid={`settle-btn-${r.restaurant_id}`}
                              >
                                Marca come saldato
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  {commissionSummary.length === 0 && (
                    <tr><td colSpan={7} className="p-8 text-center text-navy/40">Nessun ordine ancora ricevuto.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Settlement history */}
          {settlements.length > 0 && (
            <div className="bg-white p-5 rounded-2xl border border-navy/10">
              <h3 className="font-heading font-bold text-navy mb-3">Storico bonifici</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-navy/50 bg-sand-50">
                    <tr>
                      <th className="p-3">Data bonifico</th>
                      <th>Ristorante</th>
                      <th className="text-right">Ordini</th>
                      <th className="text-right">Prodotti €</th>
                      <th className="text-right">Commissione</th>
                      <th className="text-right">Netto bonificato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settlements.map(s => (
                      <tr
                        key={s.id}
                        className="border-t border-navy/5 cursor-pointer hover:bg-sand-50 transition"
                        onClick={() => setDetailSettlement(s)}
                        data-testid={`settlement-row-${s.id}`}
                      >
                        <td className="p-3 font-mono">{s.transfer_date}</td>
                        <td>{s.restaurant_name}</td>
                        <td className="text-right">{s.orders_count}</td>
                        <td className="text-right font-mono">€{s.products_total.toFixed(2)}</td>
                        <td className="text-right font-mono text-coral">-€{s.commission_total.toFixed(2)}</td>
                        <td className="text-right font-mono text-teal font-bold">€{s.net_total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="whatsapp" className="space-y-4">
          <div className="bg-white p-5 rounded-2xl border border-navy/10">
            <h3 className="font-heading font-bold text-navy text-lg mb-1">Impostazioni WhatsApp</h3>
            <p className="text-sm text-navy/60 mb-4">Configura Twilio per inviare i messaggi WhatsApp ai rider e ai clienti. Finché le credenziali sono vuote i messaggi restano in coda nel log qui sotto.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Public app URL (link accettazione ordini)</Label>
                <Input value={settingsDraft.public_app_url} onChange={e => setSettingsDraft({ ...settingsDraft, public_app_url: e.target.value })} placeholder="https://caleta-eats.example.com" data-testid="setting-public-url" />
                <p className="text-[11px] text-navy/50 mt-1">Usato per costruire i link "Accetta il pedido" nei messaggi ai rider (es. {settingsDraft.public_app_url || "https://…"}/rider/accept/CE1234).</p>
              </div>
              <div>
                <Label>Numero WhatsApp aziendale (visibile)</Label>
                <Input value={settingsDraft.whatsapp_business_number} onChange={e => setSettingsDraft({ ...settingsDraft, whatsapp_business_number: e.target.value })} placeholder="+34612345678" data-testid="setting-wa-business" />
              </div>
              <div>
                <Label>Twilio WhatsApp "From"</Label>
                <Input value={settingsDraft.twilio_whatsapp_from} onChange={e => setSettingsDraft({ ...settingsDraft, twilio_whatsapp_from: e.target.value })} placeholder="whatsapp:+14155238886" data-testid="setting-twilio-from" />
              </div>
              <div>
                <Label>Twilio Account SID</Label>
                <Input value={settingsDraft.twilio_account_sid} onChange={e => setSettingsDraft({ ...settingsDraft, twilio_account_sid: e.target.value })} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="setting-twilio-sid" />
              </div>
              <div>
                <Label>Twilio Auth Token {platformSettings.twilio_auth_token_set && <span className="text-[10px] text-teal font-bold">(salvato {platformSettings.twilio_auth_token_masked})</span>}</Label>
                <Input type="password" value={settingsDraft.twilio_auth_token} onChange={e => setSettingsDraft({ ...settingsDraft, twilio_auth_token: e.target.value })} placeholder={platformSettings.twilio_auth_token_set ? "Lascia vuoto per non modificare" : "Incolla il token"} data-testid="setting-twilio-token" />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={saveSettings} disabled={savingSettings} className="btn-coral" data-testid="save-whatsapp-settings">
                {savingSettings ? "Salvataggio…" : "Salva impostazioni"}
              </Button>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-navy/10">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-navy text-lg">Registro messaggi WhatsApp</h3>
              <Button variant="outline" size="sm" onClick={loadWhatsappSettings}>Aggiorna</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-navy/50 uppercase">
                  <tr><th className="py-2">Quando</th><th>Tipo</th><th>Destinatario</th><th>Stato</th><th>Messaggio</th></tr>
                </thead>
                <tbody>
                  {whatsappLog.map(l => (
                    <tr key={l.id} className="border-t border-navy/5 align-top" data-testid={`wa-log-${l.id}`}>
                      <td className="py-2 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="whitespace-nowrap">{l.meta?.kind || "—"}</td>
                      <td className="font-mono whitespace-nowrap">{l.to}</td>
                      <td>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${
                          l.status === "sent" ? "bg-teal/15 text-teal" :
                          l.status?.startsWith("error") || l.status === "exception" ? "bg-coral/15 text-coral" :
                          "bg-sand-100 text-navy/60"
                        }`}>{l.status}</span>
                      </td>
                      <td className="text-navy/70 whitespace-pre-wrap max-w-md truncate">{l.body?.slice(0, 140)}{l.body?.length > 140 ? "…" : ""}</td>
                    </tr>
                  ))}
                  {whatsappLog.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-navy/40">Nessun messaggio ancora inviato.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="qr" className="space-y-4">
          <div className="bg-white p-5 rounded-2xl border border-navy/10">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-coral/10 text-coral flex items-center justify-center shrink-0">
                <QrCode className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-navy text-lg">Hotel QR generator</h3>
                <p className="text-sm text-navy/60 mt-0.5">Print these QR codes and place them in hotel rooms. Scanning opens the tourist landing page — optionally personalised with the hotel name.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end mb-6">
              <div>
                <Label htmlFor="qr-hotel-input">Hotel name (optional)</Label>
                <Input
                  id="qr-hotel-input"
                  value={qrHotel}
                  onChange={(e) => setQrHotel(e.target.value)}
                  placeholder="e.g. Elba Carlota Beach Hotel"
                  data-testid="qr-hotel-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setQrHotel("")}
                className="rounded-full"
                data-testid="qr-hotel-clear"
              >
                Clear
              </Button>
            </div>

            {(() => {
              const base = typeof window !== "undefined" ? window.location.origin : "";
              const url = qrHotel.trim()
                ? `${base}/welcome?hotel=${encodeURIComponent(qrHotel.trim())}`
                : `${base}/welcome`;
              const downloadQr = () => {
                const canvas = document.getElementById("admin-qr-canvas");
                if (!canvas) return;
                const link = document.createElement("a");
                link.download = `caleta-eats-qr-${qrHotel.trim().replace(/\s+/g, "-").toLowerCase() || "welcome"}.png`;
                link.href = canvas.toDataURL("image/png");
                link.click();
              };
              return (
                <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-center">
                  <div className="bg-white p-4 rounded-2xl border-2 border-navy/10 inline-block" data-testid="qr-preview">
                    <QRCodeCanvas
                      id="admin-qr-canvas"
                      value={url}
                      size={220}
                      level="H"
                      includeMargin
                      fgColor="#0B192C"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs uppercase text-navy/50 font-bold mb-1">Landing URL</div>
                    <div className="font-mono text-sm text-navy break-all bg-sand-50 p-3 rounded-lg" data-testid="qr-url">{url}</div>
                    <div className="flex gap-2 mt-4 flex-wrap">
                      <Button onClick={downloadQr} className="btn-coral" data-testid="qr-download-btn">
                        <Download className="w-4 h-4 mr-1" /> Download PNG
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => { navigator.clipboard.writeText(url); toast.success("URL copied"); }}
                        className="rounded-full"
                        data-testid="qr-copy-btn"
                      >
                        Copy URL
                      </Button>
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-full border border-navy/20 hover:bg-navy hover:text-white text-sm px-4 py-2 transition-colors"
                        data-testid="qr-preview-link"
                      >
                        Preview landing
                      </a>
                    </div>
                    <p className="text-xs text-navy/50 mt-4">Tip: Print at 8×8 cm minimum for reliable scanning from bedside tables.</p>
                  </div>
                </div>
              );
            })()}
          </div>
        </TabsContent>
      </Tabs>

      {/* Settlement (bonifico) detail dialog */}
      <Dialog open={!!detailSettlement} onOpenChange={(v) => !v && setDetailSettlement(null)}>
        <DialogContent className="max-w-lg" data-testid="settlement-detail-dialog">
          <DialogHeader>
            <DialogTitle>Bonifico — {detailSettlement?.restaurant_name}</DialogTitle>
          </DialogHeader>
          {detailSettlement && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-sand-50 p-3 rounded-xl">
                  <div className="text-[10px] uppercase text-navy/50">Data bonifico</div>
                  <div className="font-heading font-bold text-navy" data-testid="settlement-detail-date">{detailSettlement.transfer_date}</div>
                </div>
                <div className="bg-sand-50 p-3 rounded-xl">
                  <div className="text-[10px] uppercase text-navy/50">Registrato il</div>
                  <div className="text-navy text-xs">{new Date(detailSettlement.created_at).toLocaleString("it-IT")}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-navy/5 p-3 rounded-xl text-center">
                  <div className="text-[10px] uppercase text-navy/50">Ordini</div>
                  <div className="font-heading font-bold text-navy text-lg">{detailSettlement.orders_count}</div>
                </div>
                <div className="bg-coral/10 p-3 rounded-xl text-center">
                  <div className="text-[10px] uppercase text-coral font-bold">Commissione</div>
                  <div className="font-heading font-bold text-coral text-lg">-€{detailSettlement.commission_total.toFixed(2)}</div>
                </div>
                <div className="bg-teal/10 p-3 rounded-xl text-center">
                  <div className="text-[10px] uppercase text-teal font-bold">Netto</div>
                  <div className="font-heading font-bold text-teal text-lg" data-testid="settlement-detail-net">€{detailSettlement.net_total.toFixed(2)}</div>
                </div>
              </div>
              <div className="bg-white border border-navy/10 rounded-xl p-3">
                <div className="text-[10px] uppercase text-navy/50 mb-1">Ordini inclusi ({detailSettlement.order_numbers?.length || 0})</div>
                <div className="flex flex-wrap gap-1">
                  {(detailSettlement.order_numbers || []).map(n => (
                    <span key={n} className="font-mono text-xs bg-sand-100 px-2 py-0.5 rounded">#{n}</span>
                  ))}
                </div>
              </div>
              <div className="text-[11px] text-navy/50">Prodotti totali: <span className="font-mono">€{detailSettlement.products_total.toFixed(2)}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review detail dialog */}
      <Dialog open={!!detailReview} onOpenChange={(v) => !v && setDetailReview(null)}>
        <DialogContent className="max-w-lg" data-testid="review-detail-dialog">
          <DialogHeader>
            <DialogTitle>Recensione — #{detailReview?.order_number}</DialogTitle>
          </DialogHeader>
          {detailReview && (
            <div className="space-y-3 text-sm">
              <div className="bg-sand-50 p-3 rounded-xl">
                <div className="font-heading font-bold text-navy">{detailReview.customer_first_name || "Guest"}</div>
                <div className="text-xs text-navy/60">{new Date(detailReview.created_at).toLocaleString("it-IT")} · {detailReview.restaurant_name}</div>
              </div>
              <div className="grid gap-2">
                <div className="p-3 bg-white border border-navy/10 rounded-xl">
                  <div className="text-[11px] uppercase text-navy/50 font-bold">Ristorante <span className="text-teal">(pubblico)</span></div>
                  <StarRating value={detailReview.restaurant_rating} readOnly size="md" />
                  {detailReview.restaurant_comment && <p className="text-sm text-navy/70 mt-1 italic">"{detailReview.restaurant_comment}"</p>}
                </div>
                <div className="p-3 bg-white border border-navy/10 rounded-xl">
                  <div className="text-[11px] uppercase text-navy/50 font-bold">Rider <span className="text-coral">(solo admin)</span></div>
                  {detailReview.rider_rating ? <StarRating value={detailReview.rider_rating} readOnly size="md" /> : <span className="text-xs text-navy/40">Non valutato</span>}
                  {detailReview.rider_comment && <p className="text-sm text-navy/70 mt-1 italic">"{detailReview.rider_comment}"</p>}
                </div>
                <div className="p-3 bg-white border border-navy/10 rounded-xl">
                  <div className="text-[11px] uppercase text-navy/50 font-bold">App <span className="text-coral">(solo admin)</span></div>
                  {detailReview.app_rating ? <StarRating value={detailReview.app_rating} readOnly size="md" /> : <span className="text-xs text-navy/40">Non valutato</span>}
                  {detailReview.app_comment && <p className="text-sm text-navy/70 mt-1 italic">"{detailReview.app_comment}"</p>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rider detail dialog with settlement */}
      <Dialog open={!!riderDetail} onOpenChange={(v) => !v && setRiderDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="rider-detail-dialog">
          <DialogHeader>
            <DialogTitle>Dettaglio rider · {riderDetail?.rider?.name}</DialogTitle>
          </DialogHeader>
          {riderDetail && !riderDetailLoading && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-4 gap-3 text-sm">
                <div className="bg-sand-50 p-3 rounded-xl sm:col-span-2">
                  <div className="text-[10px] uppercase text-navy/50">Contatto</div>
                  <div className="font-heading font-bold text-navy">{riderDetail.rider.name}</div>
                  <div className="text-xs text-navy/60">{riderDetail.rider.email}</div>
                  <div className="text-xs font-mono text-navy/70 mt-0.5">{riderDetail.rider.phone || "— nessun telefono —"}</div>
                </div>
                <div className="bg-coral/10 p-3 rounded-xl">
                  <div className="text-[10px] uppercase text-coral font-bold">Da saldare</div>
                  <div className="font-heading font-extrabold text-coral text-xl" data-testid="rider-detail-unpaid">€{riderDetail.totals.unpaid_total.toFixed(2)}</div>
                  <div className="text-[10px] text-navy/60">{riderDetail.totals.unpaid_count} consegne</div>
                </div>
                <div className="bg-teal/10 p-3 rounded-xl">
                  <div className="text-[10px] uppercase text-teal font-bold">Saldato</div>
                  <div className="font-heading font-extrabold text-teal text-xl" data-testid="rider-detail-paid">€{riderDetail.totals.paid_total.toFixed(2)}</div>
                  <div className="text-[10px] text-navy/60">Totale periodo €{riderDetail.totals.all_time_total.toFixed(2)}</div>
                </div>
              </div>

              <div className="border border-navy/10 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-sand-50 text-left uppercase text-navy/50">
                    <tr>
                      <th className="p-2 w-8">
                        <input
                          type="checkbox"
                          data-testid="rider-detail-select-all"
                          checked={
                            riderDetail.orders.filter(o => !o.rider_paid_at).length > 0 &&
                            selectedRiderOrders.length === riderDetail.orders.filter(o => !o.rider_paid_at).length
                          }
                          onChange={e => {
                            if (e.target.checked) setSelectedRiderOrders(riderDetail.orders.filter(o => !o.rider_paid_at).map(o => o.order_number));
                            else setSelectedRiderOrders([]);
                          }}
                        />
                      </th>
                      <th className="p-2">Consegnato</th>
                      <th className="p-2">Ordine</th>
                      <th className="p-2">Ristorante</th>
                      <th className="p-2 text-right">Subtot.</th>
                      <th className="p-2 text-right">Fisso</th>
                      <th className="p-2 text-right">3%</th>
                      <th className="p-2 text-right">Compenso</th>
                      <th className="p-2 text-center">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {riderDetail.orders.map(o => {
                      const paid = !!o.rider_paid_at;
                      const fixed = o.rider_commission_fixed ?? 2.5;
                      const pct = o.rider_commission_pct ?? 0.03;
                      const pctAmount = +((o.subtotal || 0) * pct).toFixed(2);
                      const amount = o.rider_commission_amount ?? +(fixed + pctAmount).toFixed(2);
                      const selected = selectedRiderOrders.includes(o.order_number);
                      return (
                        <tr key={o.order_number} className={`border-t border-navy/5 ${paid ? "bg-teal/5" : ""}`} data-testid={`rider-detail-order-${o.order_number}`}>
                          <td className="p-2">
                            {!paid && (
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleRiderOrderSelection(o.order_number, paid)}
                                data-testid={`rider-detail-check-${o.order_number}`}
                              />
                            )}
                          </td>
                          <td className="p-2 whitespace-nowrap text-navy/70">{new Date(o.delivered_at || o.created_at).toLocaleString()}</td>
                          <td className="p-2 font-mono">{o.order_number}</td>
                          <td className="p-2 truncate">{o.restaurant_name}</td>
                          <td className="p-2 text-right font-mono">€{(o.subtotal || 0).toFixed(2)}</td>
                          <td className="p-2 text-right font-mono text-navy/60">€{fixed.toFixed(2)}</td>
                          <td className="p-2 text-right font-mono text-navy/60">€{pctAmount.toFixed(2)}</td>
                          <td className="p-2 text-right font-mono font-bold text-navy">€{amount.toFixed(2)}</td>
                          <td className="p-2 text-center">
                            {paid ? (
                              <span className="inline-block px-2 py-0.5 rounded-full bg-teal/20 text-teal font-bold text-[10px] uppercase" data-testid={`rider-status-paid-${o.order_number}`}>Saldato · {o.rider_paid_at}</span>
                            ) : (
                              <span className="inline-block px-2 py-0.5 rounded-full bg-coral/15 text-coral font-bold text-[10px] uppercase" data-testid={`rider-status-unpaid-${o.order_number}`}>Da saldare</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {riderDetail.orders.length === 0 && (
                      <tr><td colSpan={9} className="p-6 text-center text-navy/40">Nessuna consegna ancora completata.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {riderDetail.totals.unpaid_count > 0 && (
                <div className="bg-white border-2 border-coral/30 rounded-xl p-4">
                  <div className="font-heading font-bold text-navy mb-2">Segna come Saldato</div>
                  <div className="grid sm:grid-cols-[1fr,180px,auto] gap-3 items-end">
                    <div>
                      <Label className="text-xs">Ordini selezionati</Label>
                      <div className="text-sm text-navy">
                        <span className="font-bold" data-testid="rider-settle-count">{selectedRiderOrders.length}</span> consegne — Totale
                        <span className="font-heading font-bold text-teal ml-1" data-testid="rider-settle-total">
                          €{selectedRiderOrders.reduce((s, num) => {
                            const o = riderDetail.orders.find(x => x.order_number === num);
                            return s + (o?.rider_commission_amount || 0);
                          }, 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Data pagamento</Label>
                      <Input
                        type="date"
                        value={riderPaidAt}
                        onChange={e => setRiderPaidAt(e.target.value)}
                        data-testid="rider-settle-date"
                      />
                    </div>
                    <Button
                      onClick={confirmRiderSettlement}
                      disabled={settlingRider || selectedRiderOrders.length === 0}
                      className="btn-coral h-10"
                      data-testid="rider-settle-confirm"
                    >
                      {settlingRider ? "Invio…" : "Conferma Saldato"}
                    </Button>
                  </div>
                  <p className="text-[11px] text-navy/50 mt-2">Alla conferma, il rider riceve un messaggio WhatsApp in spagnolo con la data del pagamento e il riepilogo delle consegne saldate.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Order details dialog */}
      <Dialog open={!!detailOrder} onOpenChange={(v) => !v && setDetailOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">Ordine #{detailOrder?.order_number}</DialogTitle>
            {detailOrder?.created_at && (
              <div className="text-xs text-navy/60 font-normal" data-testid="detail-created-at">
                Ordinato il <span className="font-semibold text-navy">{new Date(detailOrder.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}</span>
                {detailOrder.accepted_at && <> · Preso in carico alle <span className="font-semibold text-navy">{new Date(detailOrder.accepted_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span></>}
                {detailOrder.delivered_at && <> · Consegnato alle <span className="font-semibold text-teal">{new Date(detailOrder.delivered_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span></>}
              </div>
            )}
          </DialogHeader>
          {detailOrder && (() => {
            const sub = detailOrder.subtotal || 0;
            const discount = detailOrder.discount || 0;
            const deliveryFee = detailOrder.delivery_fee || 0;
            const commissionPct = detailOrder.commission_pct ?? 0.20;
            const commission = detailOrder.commission_amount ?? +(sub * commissionPct).toFixed(2);
            const netRestaurant = detailOrder.restaurant_net ?? +(sub - commission - discount).toFixed(2);
            return (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="bg-sand-50 p-3 rounded-xl">
                    <div className="text-xs uppercase text-navy/50">Ristorante</div>
                    <div className="font-heading font-bold text-navy">{detailOrder.restaurant_name}</div>
                  </div>
                  <div className="bg-sand-50 p-3 rounded-xl">
                    <div className="text-xs uppercase text-navy/50">Cliente</div>
                    <div className="font-heading font-bold text-navy">{detailOrder.customer?.first_name} {detailOrder.customer?.last_name}</div>
                    <div className="text-xs text-navy/60">{detailOrder.customer?.phone} · {detailOrder.customer?.email}</div>
                  </div>
                  <div className="bg-sand-50 p-3 rounded-xl sm:col-span-2">
                    <div className="text-xs uppercase text-navy/50">Consegna</div>
                    <div className="text-sm text-navy">{detailOrder.delivery?.accommodation_type} · {detailOrder.delivery?.accommodation_name} {detailOrder.delivery?.room && `· Camera ${detailOrder.delivery.room}`}</div>
                    <div className="text-xs text-navy/60">{detailOrder.delivery?.address}</div>
                    {detailOrder.delivery?.instructions && <div className="text-xs text-navy/60 italic mt-1">"{detailOrder.delivery.instructions}"</div>}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase text-navy/50 mb-2">Prodotti acquistati</div>
                  <div className="border border-navy/10 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-sand-50 text-left text-xs uppercase text-navy/50">
                        <tr>
                          <th className="p-2">Qtà</th>
                          <th className="p-2">Prodotto</th>
                          <th className="p-2 text-right">Prezzo unit.</th>
                          <th className="p-2 text-right">Subtot.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detailOrder.items || []).map((it, idx) => (
                          <tr key={idx} className="border-t border-navy/5 align-top">
                            <td className="p-2 font-heading font-bold">×{it.qty}</td>
                            <td className="p-2">
                              <div className="font-medium text-navy">{it.name}</div>
                              {(it.addons?.length > 0) && (
                                <div className="text-xs text-navy/60 mt-1">
                                  <span className="uppercase tracking-wide text-[10px] text-teal font-bold">Extra:</span>{" "}
                                  {it.addons.map((a, i) => (
                                    <span key={i}>
                                      {a.name}{a.price ? ` (+€${(+a.price).toFixed(2)})` : ""}{i < it.addons.length - 1 ? ", " : ""}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {(it.removed_ingredients?.length > 0) && (
                                <div className="text-xs text-navy/60 mt-0.5">
                                  <span className="uppercase tracking-wide text-[10px] text-coral font-bold">Senza:</span>{" "}
                                  {it.removed_ingredients.join(", ")}
                                </div>
                              )}
                            </td>
                            <td className="p-2 text-right font-mono text-navy/70">€{(+it.price).toFixed(2)}</td>
                            <td className="p-2 text-right font-mono text-navy">€{(it.price * it.qty).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-navy/5 rounded-xl p-4 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-navy/70">Subtotale prodotti</span><span className="font-mono text-navy">€{sub.toFixed(2)}</span></div>
                  {discount > 0 && (
                    <div className="flex justify-between"><span className="text-navy/70">Sconto {detailOrder.promo_code ? `(${detailOrder.promo_code})` : ""}</span><span className="font-mono text-coral">-€{discount.toFixed(2)}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-navy/70">Costo di consegna</span><span className="font-mono text-navy">€{deliveryFee.toFixed(2)}</span></div>
                  <div className="flex justify-between pt-2 border-t border-navy/10 font-heading font-bold">
                    <span className="text-navy">Totale ordine (pagato dal cliente)</span>
                    <span className="font-mono text-navy text-lg">€{(detailOrder.total || 0).toFixed(2)}</span>
                  </div>
                </div>

                <div className="border-2 border-teal/30 bg-teal/5 rounded-xl p-4 space-y-1.5 text-sm">
                  <div className="text-xs uppercase text-teal font-bold tracking-wide mb-1">Ripartizione piattaforma</div>
                  <div className="flex justify-between"><span className="text-navy/70">Commissione piattaforma ({Math.round(commissionPct * 100)}% sul subtotale)</span><span className="font-mono text-coral font-bold">-€{commission.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-navy/70">Costo di consegna (trattenuto)</span><span className="font-mono text-navy">€{deliveryFee.toFixed(2)}</span></div>
                  <div className="flex justify-between pt-2 border-t border-teal/20 font-heading font-bold">
                    <span className="text-navy">Netto al ristorante</span>
                    <span className="font-mono text-teal text-lg" data-testid="detail-net-restaurant">€{netRestaurant.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Restaurant edit dialog */}
      <Dialog open={!!settleTarget} onOpenChange={(v) => !v && setSettleTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Conferma bonifico</DialogTitle></DialogHeader>
          {settleTarget && (
            <div className="space-y-4">
              <div className="bg-sand-50 p-3 rounded-xl">
                <div className="font-heading font-bold text-navy">{settleTarget.restaurant_name}</div>
                <div className="text-xs text-navy/50 mt-0.5">{settleTarget.restaurant_email || "no email set — email non verrà inviata"}</div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center text-xs">
                <div><div className="text-navy/50">Ordini</div><div className="font-heading font-bold text-navy text-base">{settleTarget.pending.orders_count}</div></div>
                <div><div className="text-navy/50">Commissione</div><div className="font-heading font-bold text-coral text-base">-€{settleTarget.pending.commission_total.toFixed(2)}</div></div>
                <div><div className="text-navy/50">Netto</div><div className="font-heading font-bold text-teal text-base">€{settleTarget.pending.net_total.toFixed(2)}</div></div>
              </div>
              <div>
                <Label htmlFor="transfer-date">Data del bonifico bancario</Label>
                <Input
                  id="transfer-date"
                  type="date"
                  value={transferDate}
                  onChange={(e) => setTransferDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  data-testid="transfer-date-input"
                />
                <p className="text-[11px] text-navy/50 mt-1">Al conferma, il ristorante riceverà un'email con la conferma pagamento e l'elenco degli ordini.</p>
              </div>
              <div className="flex gap-2 justify-end pt-2 border-t border-navy/10">
                <Button variant="outline" onClick={() => setSettleTarget(null)}>Annulla</Button>
                <Button onClick={confirmSettle} disabled={settling || !transferDate} className="btn-coral" data-testid="confirm-settle-btn">
                  {settling ? "Invio..." : "Conferma e invia email"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Restaurant edit dialog */}
      <Dialog open={!!editRest} onOpenChange={(v) => !v && setEditRest(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editRest?.id ? "Edit" : "Add"} restaurant</DialogTitle></DialogHeader>
          {editRest && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={editRest.name} onChange={e => setEditRest({ ...editRest, name: e.target.value })} /></div>
              <div><Label>Slug</Label><Input value={editRest.slug} onChange={e => setEditRest({ ...editRest, slug: e.target.value })} /></div>
              <div><Label>Cuisine</Label>
                <Select value={editRest.cuisine} onValueChange={v => setEditRest({ ...editRest, cuisine: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CUISINES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Rating</Label><Input type="number" step="0.1" value={editRest.rating} onChange={e => setEditRest({ ...editRest, rating: Number(e.target.value) })} /></div>
              <div><Label>Restaurant email</Label><Input type="email" value={editRest.email || ""} onChange={e => setEditRest({ ...editRest, email: e.target.value })} data-testid="edit-restaurant-email" placeholder="orders@restaurant.com" /></div>
              <div><Label>Phone</Label><Input value={editRest.phone || ""} onChange={e => setEditRest({ ...editRest, phone: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Cover image URL</Label><Input value={editRest.cover_image} onChange={e => setEditRest({ ...editRest, cover_image: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Logo URL</Label><Input value={editRest.logo} onChange={e => setEditRest({ ...editRest, logo: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Description</Label><Textarea value={editRest.description} onChange={e => setEditRest({ ...editRest, description: e.target.value })} /></div>
              <div><Label>Delivery time</Label><Input value={editRest.delivery_time} onChange={e => setEditRest({ ...editRest, delivery_time: e.target.value })} /></div>
              <div><Label>Delivery fee €</Label><Input type="number" step="0.5" value={editRest.delivery_fee} onChange={e => setEditRest({ ...editRest, delivery_fee: Number(e.target.value) })} /></div>
              <div><Label>Min order €</Label><Input type="number" step="0.5" value={editRest.min_order} onChange={e => setEditRest({ ...editRest, min_order: Number(e.target.value) })} /></div>
              <div><Label>Featured (In Primo Piano)</Label>
                <Select value={String(editRest.featured)} onValueChange={v => setEditRest({ ...editRest, featured: v === "true" })}>
                  <SelectTrigger data-testid="admin-featured-toggle"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Featured — paid days</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={editRest.featured_days || 0}
                  onChange={e => {
                    const days = Math.max(0, parseInt(e.target.value || "0", 10));
                    const until = days > 0
                      ? new Date(Date.now() + days * 86400000).toISOString().slice(0, 10)
                      : null;
                    setEditRest({ ...editRest, featured_days: days, featured_until: until });
                  }}
                  data-testid="admin-featured-days"
                />
                <p className="text-[11px] text-navy/50 mt-1">
                  {editRest.featured_until ? `Runs until ${editRest.featured_until}` : "No active sponsorship"}
                </p>
              </div>
              <div className="sm:col-span-2">
                <Label>Opening hours (per day)</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map(d => (
                    <div key={d} className="flex gap-1 items-center">
                      <span className="text-xs uppercase w-8">{d}</span>
                      <Input className="h-8" value={editRest.opening_hours?.[d]?.open || ""} onChange={e => setEditRest({ ...editRest, opening_hours: { ...editRest.opening_hours, [d]: { ...editRest.opening_hours?.[d], open: e.target.value } } })} placeholder="12:00" />
                      <Input className="h-8" value={editRest.opening_hours?.[d]?.close || ""} onChange={e => setEditRest({ ...editRest, opening_hours: { ...editRest.opening_hours, [d]: { ...editRest.opening_hours?.[d], close: e.target.value } } })} placeholder="23:00" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2 flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditRest(null)}>{t("cancel")}</Button>
                <Button onClick={saveRest} className="btn-coral" data-testid="save-restaurant-btn">{t("save")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Menu item edit dialog */}
      <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editItem?.id ? "Edit" : "Add"} menu item</DialogTitle></DialogHeader>
          {editItem && (
            <div className="grid gap-3">
              <div><Label>Name</Label><Input value={editItem.name} onChange={e => setEditItem({ ...editItem, name: e.target.value })} data-testid="admin-dish-name" /></div>
              <div><Label>Category</Label>
                <Input list="ce-admin-cat-list" value={editItem.category} onChange={e => setEditItem({ ...editItem, category: e.target.value })} placeholder="Pizza, Starters, Drinks, Desserts…" data-testid="admin-dish-category" />
                <datalist id="ce-admin-cat-list">{CATEGORY_SUGGESTIONS.map(c => <option key={c} value={c} />)}</datalist>
                <p className="text-[11px] text-navy/50 mt-1">Common: Pizza, Starters (Antipasti), Drinks (Bibite), Desserts (Dolci), Popular</p>
              </div>
              <div><Label>Price €</Label><Input type="number" step="0.1" value={editItem.price} onChange={e => setEditItem({ ...editItem, price: Number(e.target.value) })} data-testid="admin-dish-price" /></div>
              <div><Label>Description</Label><Textarea value={editItem.description} onChange={e => setEditItem({ ...editItem, description: e.target.value })} data-testid="admin-dish-description" /></div>
              <div><Label>Image URL</Label>
                <Input value={editItem.image} onChange={e => setEditItem({ ...editItem, image: e.target.value })} data-testid="admin-dish-image" />
                {editItem.image && <img src={editItem.image} alt="" className="mt-2 w-full h-32 object-cover rounded-lg" />}
              </div>

              {/* Removable ingredients */}
              <div className="border-t border-navy/10 pt-3">
                <Label className="flex items-center justify-between">
                  <span>Removable ingredients</span>
                  <span className="text-[11px] font-normal text-navy/50">Customers can tick to exclude</span>
                </Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(editItem.removable_ingredients || []).map((ing, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-sand-100 text-navy text-xs rounded-full pl-3 pr-1 py-1" data-testid={`admin-ing-chip-${i}`}>
                      {ing}
                      <button
                        type="button"
                        onClick={() => setEditItem({ ...editItem, removable_ingredients: editItem.removable_ingredients.filter((_, idx) => idx !== i) })}
                        className="w-5 h-5 rounded-full bg-white hover:bg-coral hover:text-white flex items-center justify-center"
                      >×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="e.g. Mushrooms"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.target.value.trim()) {
                        e.preventDefault();
                        setEditItem({ ...editItem, removable_ingredients: [...(editItem.removable_ingredients || []), e.target.value.trim()] });
                        e.target.value = "";
                      }
                    }}
                    data-testid="admin-ing-input"
                  />
                  <span className="text-[11px] text-navy/40 self-center whitespace-nowrap">Enter to add</span>
                </div>
              </div>

              {/* Add-ons editor */}
              <div className="border-t border-navy/10 pt-3">
                <Label className="flex items-center justify-between">
                  <span>Extra add-ons (with price)</span>
                  <span className="text-[11px] font-normal text-navy/50">Extra income per order</span>
                </Label>
                <div className="space-y-1.5 mt-2">
                  {(editItem.addons || []).map((a, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`admin-addon-row-${i}`}>
                      <Input
                        value={a.name}
                        onChange={e => {
                          const next = [...editItem.addons]; next[i] = { ...next[i], name: e.target.value };
                          setEditItem({ ...editItem, addons: next });
                        }}
                        placeholder="Add-on name"
                        className="flex-1"
                      />
                      <Input
                        type="number" step="0.1"
                        value={a.price}
                        onChange={e => {
                          const next = [...editItem.addons]; next[i] = { ...next[i], price: Number(e.target.value) };
                          setEditItem({ ...editItem, addons: next });
                        }}
                        placeholder="€"
                        className="w-24"
                      />
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setEditItem({ ...editItem, addons: editItem.addons.filter((_, idx) => idx !== i) })}
                      ><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button" variant="outline" size="sm" className="mt-2"
                  onClick={() => setEditItem({ ...editItem, addons: [...(editItem.addons || []), { name: "", price: 1.0 }] })}
                  data-testid="admin-add-addon-btn"
                ><Plus className="w-3.5 h-3.5 mr-1" /> Add add-on</Button>
              </div>

              <div className="flex gap-4 flex-wrap pt-2 border-t border-navy/10">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editItem.is_popular} onChange={e => setEditItem({ ...editItem, is_popular: e.target.checked })} /> Popular</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editItem.is_family_deal} onChange={e => setEditItem({ ...editItem, is_family_deal: e.target.checked })} /> Family deal</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editItem.is_active !== false} onChange={e => setEditItem({ ...editItem, is_active: e.target.checked })} /> Visible to customers</label>
              </div>
              <div className="flex gap-2 justify-end sticky bottom-0 bg-white pt-3 border-t border-navy/10">
                <Button variant="outline" onClick={() => setEditItem(null)}>{t("cancel")}</Button>
                <Button onClick={saveItem} className="btn-coral" data-testid="save-item-btn">{t("save")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
